import { type JSX, type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import { PickerSearchBox } from '@/features/microsoft/PickerSearchBox.js';
import {
  listSharePointSetupSites,
  locateSharePointPlace,
  searchSharePointSetupItems,
  type SharePointLocated,
  type SharePointSearchHit,
  type SharePointSite,
} from '@/lib/api/sharepoint.js';

import {
  SharePointSearchResultList,
  SharePointSiteList,
} from './BrowseLevelList.js';
import type { BrowseList } from './browse-levels.js';

/** Said under every result list: search leaves places out, and a link is the way to those. */
export const PASTE_A_LINK_HINT =
  'Not here? Search does not reach every site. Open it in SharePoint, copy the link from the address bar and paste it above.';

/** What a finished search found. */
interface FoundPlaces {
  query: string;
  sites: SharePointSite[];
  hits: SharePointSearchHit[];
  nextOffset: number | null;
}

/** Whether the text is a link to open rather than words to search for. */
function isLink(text: string): boolean {
  return /^https?:\/\//i.test(text.trim());
}

function errorText(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

/** A search result as a list the level components can draw, with nothing loading. */
function resultList<T>(
  rows: T[],
  hasMore: boolean,
  loadingMore: boolean,
  onLoadMore: () => void,
): BrowseList<T> {
  return {
    rows,
    isLoading: false,
    isError: false,
    error: undefined,
    hasMore,
    isLoadingMore: loadingMore,
    loadMore: onLoadMore,
  };
}

/** A site from the results, as a located place at the site level. */
function locatedSite(site: SharePointSite): SharePointLocated {
  return {
    site,
    drive: null,
    ancestors: [],
    pathComplete: true,
    target: {
      level: 'site',
      id: site.siteId,
      name: site.name,
      webUrl: site.webUrl,
    },
  };
}

/**
 * The search box above the place tree: words search sites by name and files and folders by name
 * and content, and a pasted link opens the place it points at.
 *
 * A result is not added from here. Picking one opens the level that holds it in the tree, with the
 * result first — the tree is where a row knows whether a place on the list already covers it, and
 * a row here, found out of context, could not say.
 *
 * The link is the part that matters most. The tree's site list IS Microsoft Search, and search
 * leaves sites out — one hidden from search results never appears, although its link opens it —
 * so for those a link is the only way in, and every result list says so.
 */
export function PlaceFinder({
  workspaceId,
  connectionId,
  setupToken,
  disabled,
  onReveal,
  children,
}: {
  workspaceId: string;
  connectionId: string;
  setupToken: string;
  /** Set while the tree is busy, so a search cannot race a level that is loading. */
  disabled: boolean;
  /**
   * Opens the level that holds a found place, with the place first. Answers whether it did, so a
   * failed open keeps the results on screen instead of dropping the one that was picked.
   */
  onReveal: (located: SharePointLocated) => Promise<boolean>;
  /**
   * The tree, shown whenever no search is on screen. It is inert while a search, a link or a Show
   * is in flight, so a browse started meanwhile cannot race the answer into the same tree.
   */
  children: ReactNode;
}): JSX.Element {
  const [text, setText] = useState('');
  const [found, setFound] = useState<FoundPlaces | null>(null);
  const [pending, setPending] = useState<'find' | 'more' | null>(null);
  const [error, setError] = useState<string | null>(null);

  function clear(): void {
    setText('');
    setFound(null);
    setError(null);
  }

  async function submit(): Promise<void> {
    const query = text.trim();
    if (!query || pending) return;
    setPending('find');
    setError(null);
    try {
      if (isLink(query)) {
        await reveal(
          await locateSharePointPlace(
            { url: query },
            workspaceId,
            connectionId,
            setupToken,
          ),
        );
        return;
      }
      const [sites, hits] = await Promise.all([
        listSharePointSetupSites(workspaceId, connectionId, setupToken, query),
        searchSharePointSetupItems(
          query,
          workspaceId,
          connectionId,
          setupToken,
        ),
      ]);
      setFound({
        query,
        sites: sites.items,
        hits: hits.items,
        nextOffset: hits.nextOffset,
      });
    } catch (reason) {
      setFound(null);
      setError(
        errorText(
          reason,
          isLink(query) ? 'Could not open that link.' : 'Search failed.',
        ),
      );
    } finally {
      setPending(null);
    }
  }

  /**
   * The next page of hits, added only to the search it was asked for: a Clear or a new search while
   * it was in flight must not bring the old results back.
   */
  async function loadMore(): Promise<void> {
    if (!found || found.nextOffset === null || pending) return;
    const query = found.query;
    setPending('more');
    setError(null);
    try {
      const page = await searchSharePointSetupItems(
        found.query,
        workspaceId,
        connectionId,
        setupToken,
        { offset: found.nextOffset },
      );
      setFound((current) =>
        current?.query === query
          ? {
              ...current,
              hits: [...current.hits, ...page.items],
              nextOffset: page.nextOffset,
            }
          : current,
      );
    } catch (reason) {
      setError(errorText(reason, 'Could not load more.'));
    } finally {
      setPending(null);
    }
  }

  /** Hands a found place to the tree and puts the tree back on screen. */
  async function reveal(located: SharePointLocated): Promise<void> {
    if (await onReveal(located)) clear();
  }

  /** Opens a file or folder hit in the tree, after asking where it lives. */
  async function revealHit(address: {
    driveId: string;
    itemId: string;
  }): Promise<void> {
    if (pending) return;
    setPending('find');
    setError(null);
    try {
      await reveal(
        await locateSharePointPlace(
          { driveId: address.driveId, itemId: address.itemId },
          workspaceId,
          connectionId,
          setupToken,
        ),
      );
    } catch (reason) {
      setError(errorText(reason, 'Could not open this.'));
    } finally {
      setPending(null);
    }
  }

  const nothingFound =
    found !== null && found.sites.length === 0 && found.hits.length === 0;

  return (
    <>
      <PickerSearchBox
        value={text}
        onChange={setText}
        onSubmit={() => void submit()}
        onClear={clear}
        placeholder="Search, or paste a SharePoint link"
        label="Search SharePoint, or paste a link"
        searching={pending !== null}
        showClear={Boolean(text || found)}
        disabled={disabled}
      />
      {error && (
        <p className="shrink-0 border-b border-border px-2 py-2 text-sm text-destructive">
          {error}
        </p>
      )}
      {found === null ? (
        <div className="contents" inert={pending !== null}>
          {children}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
          {nothingFound ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              Nothing in SharePoint matched this search.
            </p>
          ) : (
            <>
              {found.sites.length > 0 && (
                <>
                  <p className="px-2 pt-1 text-xs font-medium text-muted-foreground">
                    Sites
                  </p>
                  <SharePointSiteList
                    list={resultList(found.sites, false, false, () => {})}
                    onPick={(site) => {
                      if (!pending) void reveal(locatedSite(site));
                    }}
                  />
                </>
              )}
              {found.hits.length > 0 && (
                <>
                  <p className="px-2 pt-1 text-xs font-medium text-muted-foreground">
                    Files and folders
                  </p>
                  <SharePointSearchResultList
                    list={resultList(
                      found.hits,
                      found.nextOffset !== null,
                      pending === 'more',
                      () => void loadMore(),
                    )}
                    connectionId={connectionId}
                    onOpenFolder={(hit) => void revealHit(hit)}
                    renderItemAction={(row) =>
                      row.item.isFolder ? null : (
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Show ${row.item.name}`}
                          disabled={pending !== null}
                          onClick={() =>
                            void revealHit({
                              driveId: row.driveId,
                              itemId: row.item.itemId,
                            })
                          }
                        >
                          Show
                        </Button>
                      )
                    }
                  />
                </>
              )}
            </>
          )}
          <p className="px-2 py-2 text-xs text-muted-foreground">
            {PASTE_A_LINK_HINT}
          </p>
        </div>
      )}
    </>
  );
}
